import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate as fmtItDate } from "@/lib/formatDate";
import {
  UserCog, TrendingUp, Users, Calendar, DollarSign, ArrowRight, Clock,
  RefreshCw, Table, LayoutDashboard, ChevronDown, Save, Trophy,
  BarChart3, AlertCircle, Info, X,
} from "lucide-react";

/* ------------------------------------------------------------------
 * Staff Performance — 2 viste:
 *  A) Riepilogo automatico (invariato, iter27): fatturato/ticket medio
 *     aggregato per intervallo di date da invoices/appointments.
 *  B) Tracker mensile (NEW iter82): Excel-style — 12 righe (Gen→Dic)
 *     per ciascun collaboratore con dati manuali (ore, stipendio, contr.,
 *     altro, pacchetti) + colonne auto (fatturato/passaggi/servizi) +
 *     KPI derivati (costo orario, fatturato orario, ritorno lordo).
 * ---------------------------------------------------------------- */

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function StaffPerformance() {
  const [tab, setTab] = useState("auto"); // default: Riepilogo — Tracker mensile è secondario

  return (
    <div data-testid="staff-performance-page" className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight flex items-center gap-3">
            <UserCog className="h-8 w-8 text-[color:var(--sp-brand)]" /> Staff Performance
          </h1>
          <p className="mt-1 text-sm text-[color:var(--sp-text-secondary)]">
            Riepilogo automatico dai tuoi scontrini · tracker mensile stile Excel su richiesta.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
          <button data-testid="staff-tab-auto" onClick={() => setTab("auto")}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${tab === "auto" ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
            <LayoutDashboard className="h-3.5 w-3.5" /> Riepilogo
          </button>
          <button data-testid="staff-tab-monthly" onClick={() => setTab("monthly")}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${tab === "monthly" ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
            <Table className="h-3.5 w-3.5" /> Tracker mensile
          </button>
        </div>
      </header>

      {tab === "monthly" ? <MonthlyTracker /> : <AutoSummary />}
    </div>
  );
}

// =====================================================================
// A) Riepilogo automatico + drill-down per collaboratore
// =====================================================================
function AutoSummary() {
  const today = new Date();
  const daysAgo = (n) => {
    const d = new Date(today); d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };
  const [since, setSince] = useState(daysAgo(30));
  const [until, setUntil] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldownStaff, setDrilldownStaff] = useState(null); // {staff_id, staff_name, staff_color}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/staff/performance?since_iso=${since}&until_iso=${until}`);
      setData(r.data);
    } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("StaffPerf load failed", e); }
    setLoading(false);
  }, [since, until]);
  useEffect(() => { load(); }, [load]);

  const [showCustom, setShowCustom] = useState(false);
  const applyPreset = (days) => { setSince(daysAgo(days)); setUntil(daysAgo(0)); setShowCustom(false); };
  const rows = data?.rows || [];
  const maxRev = Math.max(1, ...rows.map((r) => r.revenue));

  return (
    <div className="space-y-6">
      <div className="sp-card p-3 flex flex-wrap gap-2 items-center" data-testid="staff-perf-range">
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
          {[
            { d: 7,  l: "7 giorni" },
            { d: 30, l: "30 giorni" },
            { d: 90, l: "90 giorni" },
          ].map((p) => {
            const active = !showCustom && since === daysAgo(p.d);
            return (
              <button key={p.d} onClick={() => applyPreset(p.d)} data-testid={`staff-perf-preset-${p.d}`}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${active ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                {p.l}
              </button>
            );
          })}
          <button onClick={() => setShowCustom(true)} data-testid="staff-perf-preset-custom"
            className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${showCustom ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
            Personalizzato
          </button>
        </div>
        {showCustom && (
          <div className="inline-flex items-center gap-1.5">
            <input type="date" value={since} max={until} onChange={(e) => setSince(e.target.value)}
                   className="sp-input !py-1 !px-2 text-xs w-auto" data-testid="staff-perf-since" />
            <span className="text-xs text-[color:var(--sp-text-muted)]">→</span>
            <input type="date" value={until} min={since} max={daysAgo(0)}
                   onChange={(e) => setUntil(e.target.value)}
                   className="sp-input !py-1 !px-2 text-xs w-auto" data-testid="staff-perf-until" />
          </div>
        )}
        <div className="ml-auto text-[11px] text-[color:var(--sp-text-muted)]">
          {fmtItDate(since)} → {fmtItDate(until)}
        </div>
        <button onClick={load} data-testid="staff-perf-refresh"
                className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]">
          <RefreshCw className="h-3 w-3" /> Aggiorna
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TotalCard icon={DollarSign} label="Incassi totali" value={fmtEur(data.totals?.revenue)} tint="emerald" />
          <TotalCard icon={Users} label="Incassi emessi" value={data.totals?.invoices_count} />
          <TotalCard icon={Calendar} label="Appuntamenti" value={data.totals?.appointments} />
        </div>
      )}

      {/* Info convention + unassigned CTA */}
      {data && (
        <div className="flex flex-wrap gap-3 items-center">
          {data.convention_note && (
            <div className="flex-1 min-w-[280px] p-3 rounded-lg bg-cyan-50 border border-cyan-200 text-[11px] text-cyan-900 inline-flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{data.convention_note}</span>
            </div>
          )}
          {(data.unassigned_invoices_count || 0) > 0 && (
            <a href="/app/invoices?unassigned=1" data-testid="staff-unassigned-btn"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 transition">
              <AlertCircle className="h-3.5 w-3.5" />
              {data.unassigned_invoices_count} {data.unassigned_invoices_count === 1 ? "incasso senza collaboratore" : "incassi senza collaboratore"}
              <ArrowRight className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      {drilldownStaff && (
        <StaffDrilldown staff={drilldownStaff} onClose={() => setDrilldownStaff(null)} />
      )}
      <div className="sp-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center space-y-2">
            <UserCog className="h-10 w-10 mx-auto text-[color:var(--sp-text-muted)]" />
            <div className="font-display font-bold">Nessun dato nel periodo</div>
          </div>
        ) : (
          <div className="sp-table-wrap">
          <table className="w-full">
            <thead className="bg-[color:var(--sp-subtle)] text-left label-eyebrow">
              <tr>
                <th className="p-4">Collaboratore</th>
                <th className="p-4 text-right">Incassi</th>
                <th className="p-4 text-right">Ticket medio</th>
                <th className="p-4 text-right">Passaggi</th>
                <th className="p-4 text-right">Clienti unici</th>
                <th className="p-4 text-right" title="Ore in salone (working_hours × giorni − chiusure). NON prese dagli appuntamenti.">Ore in salone</th>
                <th className="p-4">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pct = (r.revenue / maxRev) * 100;
                const canDrill = !!r.staff_id;
                return (
                  <tr key={r.staff_id || "unassigned"}
                      data-testid={`staff-row-${r.staff_id || "unassigned"}`}
                      onClick={() => canDrill && setDrilldownStaff({ staff_id: r.staff_id, staff_name: r.staff_name, staff_color: r.staff_color, since, until })}
                      className={`border-t border-[color:var(--sp-border)] ${canDrill ? "cursor-pointer hover:bg-[color:var(--sp-subtle)]/40" : ""} transition`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-black"
                              style={{ background: r.staff_color }}>{(r.staff_name || "?").charAt(0).toUpperCase()}</span>
                        <div className="font-bold text-sm inline-flex items-center gap-1">
                          {r.staff_name}
                          {canDrill && <ChevronDown className="h-3 w-3 opacity-40 -rotate-90" />}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-right font-display font-black tabular-nums text-emerald-600">{fmtEur(r.revenue)}</td>
                    <td className="p-4 text-right tabular-nums text-sm">{fmtEur(r.avg_ticket)}</td>
                    <td className="p-4 text-right tabular-nums text-sm" title="Frazionale pesato sul fatturato">
                      {typeof r.passaggi === "number" ? r.passaggi.toFixed(1) : r.invoices_count}
                    </td>
                    <td className="p-4 text-right tabular-nums text-sm">{r.unique_clients}</td>
                    <td className="p-4 text-right tabular-nums text-sm inline-flex items-center gap-1 justify-end w-full">
                      <Clock className="h-3 w-3 opacity-50" /> {r.hours_worked}h
                    </td>
                    <td className="p-4 w-32">
                      <div className="h-2 rounded-full bg-[color:var(--sp-subtle)] overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: r.staff_color }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// B) Tracker mensile Excel-style (NEW iter82)
// =====================================================================
function MonthlyTracker() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null); // staff_id or null (compare mode)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Dirty-tracking per non salvare a ogni keystroke
  const [dirty, setDirty] = useState({}); // { [staff_id]: { [month]: { field: val, ... } } }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [staffRes, monthlyRes] = await Promise.all([
        api.get("/staff"),
        api.get(`/staff/perf/monthly?year=${year}`),
      ]);
      setStaffList(staffRes.data || []);
      setData(monthlyRes.data);
      if (!selectedStaff && (staffRes.data || []).length > 0) {
        setSelectedStaff(staffRes.data[0].id);
      }
    } catch (e) { toast.error("Errore caricamento dati mensili"); }
    setLoading(false);
  }, [year]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const currentStaff = useMemo(() => {
    if (!data) return null;
    return (data.staff || []).find((s) => s.staff_id === selectedStaff) || (data.staff || [])[0] || null;
  }, [data, selectedStaff]);

  const updateCell = (month, field, value) => {
    if (!currentStaff) return;
    const sid = currentStaff.staff_id;
    setDirty((d) => ({
      ...d,
      [sid]: { ...(d[sid] || {}), [month]: { ...((d[sid] || {})[month] || {}), [field]: value } },
    }));
    // Update local snapshot immediately (optimistic)
    setData((prev) => {
      if (!prev) return prev;
      const nxt = structuredClone(prev);
      const s = nxt.staff.find((x) => x.staff_id === sid);
      if (!s) return prev;
      const row = s.rows.find((r) => r.month === month);
      if (row) row[field] = Number(value) || 0;
      return nxt;
    });
  };

  const saveDirty = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    let saved = 0;
    try {
      for (const [sid, months] of Object.entries(dirty)) {
        const staff = data?.staff?.find((s) => s.staff_id === sid);
        if (!staff) continue;
        for (const [month, changes] of Object.entries(months)) {
          const monthNum = Number(month);
          const currentRow = staff.rows.find((r) => r.month === monthNum) || {};
          const payload = {
            staff_id: sid, year, month: monthNum,
            ore_lavorate: Number(currentRow.ore_lavorate || changes.ore_lavorate || 0),
            stipendio_netto: Number(currentRow.stipendio_netto || changes.stipendio_netto || 0),
            contributi: Number(currentRow.contributi || changes.contributi || 0),
            altro_in_busta: Number(currentRow.altro_in_busta || changes.altro_in_busta || 0),
            pacchetti_venduti: Number(currentRow.pacchetti_venduti || changes.pacchetti_venduti || 0),
            notes: currentRow.notes || null,
          };
          await api.put("/staff/perf/monthly", payload);
          saved++;
        }
      }
      setDirty({});
      toast.success(`${saved} righe salvate`);
      load();
    } catch (e) { toast.error("Errore salvataggio"); }
    setSaving(false);
  };

  const dirtyCount = Object.values(dirty).reduce((acc, m) => acc + Object.keys(m).length, 0);
  const rows = currentStaff?.rows || [];
  const totals = currentStaff?.totals || {};

  // Best month per KPI (per highlight)
  const bestFatt = rows.reduce((m, r) => (r.fatturato > (m?.fatturato || 0) ? r : m), null);
  const bestRoi = rows.reduce((m, r) => (r.ritorno_lordo > (m?.ritorno_lordo || 0) ? r : m), null);

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="sp-card p-4 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <span className="label-eyebrow">Anno</span>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                  className="sp-input !py-1 text-sm" data-testid="staff-monthly-year">
            {[year + 1, year, year - 1, year - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="label-eyebrow">Collaboratore</span>
          <select value={selectedStaff || ""} onChange={(e) => setSelectedStaff(e.target.value)}
                  className="sp-input !py-1 text-sm" data-testid="staff-monthly-select">
            {(data?.staff || []).map((s) => (
              <option key={s.staff_id} value={s.staff_id}>{s.staff_name}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {dirtyCount > 0 && (
            <span className="text-xs text-amber-600 font-bold">{dirtyCount} modifiche non salvate</span>
          )}
          <button onClick={saveDirty} disabled={dirtyCount === 0 || saving}
                  data-testid="staff-monthly-save"
                  className="sp-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40">
            <Save className="h-4 w-4" /> {saving ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      {currentStaff && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <MiniKpi label="Ore totali" value={`${totals.ore_lavorate?.toFixed(0) || 0}h`} />
          <MiniKpi label="Costo staff" value={fmtEur(totals.totale_costo)} tint="rose" />
          <MiniKpi label="Fatturato" value={fmtEur(totals.fatturato)} tint="emerald" />
          <MiniKpi label="Passaggi" value={typeof totals.n_passaggi === "number" ? totals.n_passaggi.toFixed(1) : (totals.n_passaggi || 0)} />
          <MiniKpi label="Servizi erogati" value={totals.servizi_erogati || 0} />
          <MiniKpi label="Pacchetti venduti" value={totals.pacchetti_venduti || 0} tint="violet" />
        </div>
      )}

      {/* Bar chart mese-per-mese: Fatturato vs Costo */}
      {currentStaff && rows.some((r) => r.fatturato > 0 || r.totale_costo > 0) && (
        <div className="sp-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-black text-sm inline-flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[color:var(--sp-brand)]" />
              Fatturato vs Costo staff — {year}
            </h3>
            <div className="text-xs inline-flex items-center gap-3">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400"></span>Fatturato</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400"></span>Costo</span>
            </div>
          </div>
          <FattVsCostoBar rows={rows} />
        </div>
      )}

      {/* Excel-style table */}
      <div className="sp-card overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>
        ) : !currentStaff ? (
          <div className="p-12 text-center text-sm">
            <div className="font-display font-bold">Nessun collaboratore</div>
            <p className="text-[color:var(--sp-text-muted)] mt-1">
              Aggiungi un membro dello staff da <a href="/app/staff" className="text-[color:var(--sp-brand)] font-bold">Staff</a> per iniziare a tracciare le performance mensili.
            </p>
          </div>
        ) : (
          <table className="w-full text-xs min-w-[1400px]" data-testid="staff-monthly-table">
            <thead>
              <tr className="bg-[color:var(--sp-subtle)] label-eyebrow">
                <th className="p-2 text-left sticky left-0 bg-[color:var(--sp-subtle)] z-10">Mese</th>
                <th className="p-2 text-right border-l border-[color:var(--sp-border)]" title="Manuale — ore effettive in salone">Ore</th>
                <th className="p-2 text-right" title="Manuale">Stipendio netto</th>
                <th className="p-2 text-right" title="Manuale">Contributi</th>
                <th className="p-2 text-right" title="Manuale — TFR, premi, buoni">Altro in busta</th>
                <th className="p-2 text-right bg-rose-50 border-l border-rose-200" title="Auto = stipendio + contributi + altro">Tot. costo</th>
                <th className="p-2 text-right bg-rose-50" title="Auto = tot. costo / ore">€/h costo</th>
                <th className="p-2 text-right bg-emerald-50 border-l border-emerald-200" title="Auto — dagli scontrini">Fatturato</th>
                <th className="p-2 text-right bg-emerald-50" title="Auto — n° distinti appuntamenti">Passaggi</th>
                <th className="p-2 text-right bg-emerald-50" title="Auto — n° servizi fatturati">Servizi</th>
                <th className="p-2 text-right bg-emerald-50" title="Auto = servizi / passaggi">Serv/pass</th>
                <th className="p-2 text-right" title="Manuale">Pacchetti</th>
                <th className="p-2 text-right bg-violet-50 border-l border-violet-200" title="Auto = pacchetti / passaggi">Tasso conv.</th>
                <th className="p-2 text-right bg-emerald-50 border-l border-emerald-200" title="Auto = fatturato / ore">€/h fatt.</th>
                <th className="p-2 text-right bg-amber-50 border-l border-amber-200" title="Auto = fatturato / tot. costo (moltiplicatore)">Ritorno lordo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const dirtyMonth = dirty[currentStaff.staff_id]?.[r.month] || {};
                const isDirty = Object.keys(dirtyMonth).length > 0;
                const isBestFatt = bestFatt && r.month === bestFatt.month && r.fatturato > 0;
                const isBestRoi = bestRoi && r.month === bestRoi.month && r.ritorno_lordo > 0;
                // Auto-fill flags: se manual==0 e auto>0 → cella auto-popolata (grigia)
                const oreAuto = (r.ore_lavorate || 0) === 0 && (r.ore_auto || 0) > 0;
                const stipAuto = (r.stipendio_netto || 0) === 0 && (r.stipendio_auto || 0) > 0;
                const contrAuto = (r.contributi || 0) === 0 && (r.contributi_auto || 0) > 0;
                const altroAuto = (r.altro_in_busta || 0) === 0 && (r.altro_in_busta_auto || 0) > 0;
                return (
                  <tr key={r.month} data-testid={`staff-monthly-row-${r.month}`}
                      className={`border-t border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]/40 ${isDirty ? "bg-amber-50/50" : ""}`}>
                    <td className={`p-2 font-bold sticky left-0 z-10 ${isDirty ? "bg-amber-50" : "bg-white"}`}>
                      {MONTHS[r.month - 1]}
                      {isDirty && <span className="ml-1 text-amber-600" title="Modifiche non salvate">●</span>}
                    </td>
                    <NumCell value={oreAuto ? r.ore_auto : r.ore_lavorate} auto={oreAuto} onChange={(v) => updateCell(r.month, "ore_lavorate", v)} testid={`ore-${r.month}`} suffix="h" step="0.5" />
                    <NumCell value={stipAuto ? r.stipendio_auto : r.stipendio_netto} auto={stipAuto} onChange={(v) => updateCell(r.month, "stipendio_netto", v)} testid={`stip-${r.month}`} eur />
                    <NumCell value={contrAuto ? r.contributi_auto : r.contributi} auto={contrAuto} onChange={(v) => updateCell(r.month, "contributi", v)} testid={`contr-${r.month}`} eur />
                    <NumCell value={altroAuto ? r.altro_in_busta_auto : r.altro_in_busta} auto={altroAuto} onChange={(v) => updateCell(r.month, "altro_in_busta", v)} testid={`altro-${r.month}`} eur />
                    <td className="p-2 text-right tabular-nums font-bold text-rose-700 bg-rose-50/40 border-l border-rose-200">{fmtEur(r.totale_costo)}</td>
                    <td className="p-2 text-right tabular-nums font-bold text-rose-700 bg-rose-50/40">{fmtEur(r.costo_orario)}</td>
                    <td className="p-2 text-right tabular-nums font-bold text-emerald-700 bg-emerald-50/40 border-l border-emerald-200">
                      {isBestFatt && <Trophy className="h-3 w-3 inline mr-1 text-amber-500" />}
                      {fmtEur(r.fatturato)}
                    </td>
                    <td className="p-2 text-right tabular-nums bg-emerald-50/40">{typeof r.n_passaggi === "number" ? r.n_passaggi.toFixed(1) : r.n_passaggi}</td>
                    <td className="p-2 text-right tabular-nums bg-emerald-50/40">{r.servizi_erogati}</td>
                    <td className="p-2 text-right tabular-nums text-[11px] bg-emerald-50/40">{r.servizi_per_passaggio.toFixed(2)}</td>
                    <NumCell value={r.pacchetti_venduti} onChange={(v) => updateCell(r.month, "pacchetti_venduti", v)} testid={`pack-${r.month}`} integer />
                    <td className="p-2 text-right tabular-nums text-[11px] font-bold text-violet-700 bg-violet-50/40 border-l border-violet-200">
                      {(r.tasso_conversione_pacchetti * 100).toFixed(1)}%
                    </td>
                    <td className="p-2 text-right tabular-nums font-bold text-emerald-700 bg-emerald-50/40 border-l border-emerald-200">{fmtEur(r.fatturato_orario)}</td>
                    <td className="p-2 text-right tabular-nums font-bold text-amber-700 bg-amber-50/40 border-l border-amber-200">
                      {isBestRoi && <Trophy className="h-3 w-3 inline mr-1 text-amber-500" />}
                      {r.ritorno_lordo > 0 ? `${r.ritorno_lordo.toFixed(2)}×` : "—"}
                    </td>
                  </tr>
                );
              })}
              {/* Total row */}
              <tr className="border-t-2 border-[color:var(--sp-border-strong)] bg-[color:var(--sp-subtle)]">
                <td className="p-2 font-black sticky left-0 z-10 bg-[color:var(--sp-subtle)]">TOTALE {year}</td>
                <td className="p-2 text-right font-black">{totals.ore_lavorate?.toFixed(0) || 0}h</td>
                <td className="p-2 text-right font-black text-[color:var(--sp-text-muted)]">—</td>
                <td className="p-2 text-right font-black text-[color:var(--sp-text-muted)]">—</td>
                <td className="p-2 text-right font-black text-[color:var(--sp-text-muted)]">—</td>
                <td className="p-2 text-right font-black text-rose-700 bg-rose-100/60 border-l border-rose-200">{fmtEur(totals.totale_costo)}</td>
                <td className="p-2 text-right font-black text-rose-700 bg-rose-100/60">
                  {totals.ore_lavorate > 0 ? fmtEur(totals.totale_costo / totals.ore_lavorate) : "—"}
                </td>
                <td className="p-2 text-right font-black text-emerald-700 bg-emerald-100/60 border-l border-emerald-200">{fmtEur(totals.fatturato)}</td>
                <td className="p-2 text-right font-black bg-emerald-100/60">{totals.n_passaggi?.toFixed?.(1) ?? totals.n_passaggi}</td>
                <td className="p-2 text-right font-black bg-emerald-100/60">{totals.servizi_erogati}</td>
                <td className="p-2 text-right bg-emerald-100/60 text-[color:var(--sp-text-muted)]">—</td>
                <td className="p-2 text-right font-black">{totals.pacchetti_venduti}</td>
                <td className="p-2 text-right font-black text-violet-700 bg-violet-100/60 border-l border-violet-200">
                  {totals.n_passaggi ? `${((totals.pacchetti_venduti / totals.n_passaggi) * 100).toFixed(1)}%` : "—"}
                </td>
                <td className="p-2 text-right font-black text-emerald-700 bg-emerald-100/60 border-l border-emerald-200">
                  {totals.ore_lavorate > 0 ? fmtEur(totals.fatturato / totals.ore_lavorate) : "—"}
                </td>
                <td className="p-2 text-right font-black text-amber-700 bg-amber-100/60 border-l border-amber-200">
                  {totals.totale_costo > 0 ? `${(totals.fatturato / totals.totale_costo).toFixed(2)}×` : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Legend */}
      <div className="text-xs text-[color:var(--sp-text-muted)] space-y-1">
        <p><span className="inline-block w-3 h-3 rounded-sm bg-white border align-middle mr-1"></span>Bianco = inserimento manuale</p>
        <p>
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 align-middle mr-1"></span>Verde = auto dai tuoi scontrini
          <span className="inline-block w-3 h-3 rounded-sm bg-rose-100 align-middle mr-1 ml-3"></span>Rosso = costi calcolati
          <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 align-middle mr-1 ml-3"></span>Ambra = ROI derivato
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
function NumCell({ value, onChange, testid, suffix, eur, integer, step, auto }) {
  const display = value == null || value === 0 ? "" : String(value);
  return (
    <td className={`p-1 ${auto ? "bg-slate-100/70" : ""}`} title={auto ? "Popolato dai costi Personale (readonly)" : undefined}>
      <div className="relative">
        {eur && <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[color:var(--sp-text-muted)]">€</span>}
        <input
          data-testid={`staff-cell-${testid}`}
          type="number" min="0" step={step || (integer ? "1" : "0.01")}
          value={display}
          onChange={(e) => onChange(e.target.value)}
          placeholder={auto ? "auto" : "0"}
          readOnly={auto}
          className={`w-full text-right text-xs tabular-nums bg-transparent border border-transparent rounded px-1.5 py-1 hover:border-[color:var(--sp-border)] focus:border-[color:var(--sp-brand)] focus:bg-white focus:outline-none ${eur ? "pl-4" : ""} ${auto ? "text-slate-500 cursor-not-allowed" : ""}`}
        />
        {suffix && !eur && <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[color:var(--sp-text-muted)]">{suffix}</span>}
        {auto && <span className="absolute -top-0.5 -right-0.5 text-[8px] text-cyan-600 font-black">auto</span>}
      </div>
    </td>
  );
}

function MiniKpi({ label, value, tint }) {
  const tintClass = tint === "emerald" ? "text-emerald-700" : tint === "rose" ? "text-rose-700" : tint === "violet" ? "text-violet-700" : "text-[color:var(--sp-text)]";
  return (
    <div className="sp-card p-3">
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`font-display font-black text-xl mt-1 tabular-nums ${tintClass}`}>{value}</div>
    </div>
  );
}

function TotalCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="sp-card p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[color:var(--sp-text-muted)]">
        <Icon className="h-3.5 w-3.5 text-[color:var(--sp-brand)]" /> {label}
      </div>
      <div className={`mt-2 text-3xl font-display font-black tabular-nums ${tint === "emerald" ? "text-emerald-600" : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Drill-down: apre modal con la lista dei passaggi in cui il collaboratore
// è intervenuto, con peso (share) e importo attribuito.
// ---------------------------------------------------------------------
function StaffDrilldown({ staff, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/staff/${staff.staff_id}/drilldown?since_iso=${staff.since}&until_iso=${staff.until}`);
        setData(r.data);
      } catch (e) { if (process.env.NODE_ENV !== "production") console.warn("drilldown failed", e); }
      setLoading(false);
    })();
  }, [staff]);

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="sp-card w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()} data-testid="staff-drilldown-modal">
        {/* Header */}
        <div className="p-6 flex items-start justify-between border-b border-[color:var(--sp-border)]"
             style={{ background: `linear-gradient(135deg, ${staff.staff_color}15, transparent)` }}>
          <div className="flex items-center gap-3">
            <span className="h-12 w-12 rounded-full flex items-center justify-center text-white font-black text-lg"
                  style={{ background: staff.staff_color }}>{staff.staff_name.charAt(0).toUpperCase()}</span>
            <div>
              <h3 className="font-display text-2xl font-black">Passaggi di {staff.staff_name}</h3>
              <p className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">Dettaglio per ogni scontrino in cui il collaboratore è intervenuto</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1"><X className="h-5 w-5" /></button>
        </div>

        {/* Totals */}
        {data && (
          <div className="grid grid-cols-3 gap-3 p-5 border-b border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/40">
            <MiniKpi
              label="Passaggi (frazionali)"
              value={typeof data.totals.passaggi_frazionali === "number" ? data.totals.passaggi_frazionali.toFixed(1) : data.totals.count}
            />
            <MiniKpi label="Incasso attribuito" value={fmtEur(data.totals.staff_amount)} tint="emerald" />
            <MiniKpi label="Scontrini condivisi" value={`${data.totals.shared_count} / ${data.totals.count}`} tint="violet" />
          </div>
        )}

        {/* List */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="p-12 text-center text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>
          ) : !data?.entries?.length ? (
            <div className="p-12 text-center text-sm text-[color:var(--sp-text-muted)]">
              Nessun passaggio in questo periodo
            </div>
          ) : (
            <div className="divide-y divide-[color:var(--sp-border)]">
              {data.entries.map((e, i) => (
                <div key={e.invoice_id} className="p-5 hover:bg-[color:var(--sp-subtle)]/30 transition"
                     data-testid={`drilldown-entry-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="font-bold text-sm inline-flex items-center gap-2 flex-wrap">
                        <span>{e.client_name || "—"}</span>
                        {e.shared_with_others && (
                          <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">
                            condiviso
                          </span>
                        )}
                        {e.appointment_id && (
                          <Link to={`/app/agenda?date=${(e.paid_at || "").slice(0,10)}&edit=${e.appointment_id}`}
                                onClick={(ev) => ev.stopPropagation()}
                                className="text-[10px] text-[color:var(--sp-brand)] hover:underline inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> apri appuntamento
                          </Link>
                        )}
                      </div>
                      <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">
                        {e.paid_at ? new Date(e.paid_at).toLocaleString("it-IT") : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-lg font-black text-emerald-600 tabular-nums">
                        {fmtEur(e.staff_amount)}
                      </div>
                      <div className="text-[10px] text-[color:var(--sp-text-muted)]">
                        {e.share_pct.toFixed(0)}% di {fmtEur(e.invoice_total)}
                      </div>
                    </div>
                  </div>
                  {/* Progress bar della share */}
                  <div className="h-2 rounded-full bg-[color:var(--sp-subtle)] overflow-hidden mb-3">
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${e.share_pct}%`, background: staff.staff_color }} />
                  </div>
                  {/* Lines */}
                  <div className="space-y-1">
                    {e.lines_for_staff.map((l, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: staff.staff_color }} />
                          {l.description}
                          <span className="text-[9px] text-[color:var(--sp-text-muted)]">×{l.quantity}</span>
                        </span>
                        <span className="tabular-nums font-bold">{fmtEur(l.amount)}</span>
                      </div>
                    ))}
                    {e.lines_other.length > 0 && (
                      <details className="text-xs mt-2">
                        <summary className="cursor-pointer text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-text)]">
                          +{e.lines_other.length} servizi/prodotti attribuiti ad altri
                        </summary>
                        <div className="mt-1 space-y-1 pl-3">
                          {e.lines_other.map((l, idx) => (
                            <div key={idx} className="flex items-center justify-between text-[color:var(--sp-text-muted)]">
                              <span>{l.description} <span className="text-[9px]">×{l.quantity}</span></span>
                              <span className="tabular-nums">{fmtEur(l.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtEur(n) {
  if (n == null || Number.isNaN(n)) return "€0";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR",
    maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2 }).format(n);
}

// Bar chart puro SVG — mese-per-mese fatturato vs costo (gradient coerente col Dashboard)
function FattVsCostoBar({ rows }) {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.fatturato || 0, r.totale_costo || 0)));
  const W = 900, H = 220, padX = 40, padY = 20, innerH = H - 2 * padY;
  const bandW = (W - 2 * padX) / rows.length;
  const barW = Math.max(6, bandW * 0.32);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56" data-testid="staff-monthly-barchart">
      <defs>
        <linearGradient id="staff-bar-fatt" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22C55E" stopOpacity="1" />
          <stop offset="100%" stopColor="#22C55E" stopOpacity="0.55" />
        </linearGradient>
        <linearGradient id="staff-bar-cost" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F43F5E" stopOpacity="1" />
          <stop offset="100%" stopColor="#F43F5E" stopOpacity="0.55" />
        </linearGradient>
      </defs>
      {/* Y grid + labels */}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = padY + innerH * (1 - t);
        return (
          <g key={t}>
            <line x1={padX} y1={y} x2={W - 10} y2={y} stroke="#E5E7EB" strokeDasharray="2 3" />
            <text x={padX - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#94A3B8">{Math.round(max * t / 1000)}k</text>
          </g>
        );
      })}
      {rows.map((r, i) => {
        const cx = padX + bandW * i + bandW / 2;
        const hF = innerH * ((r.fatturato || 0) / max);
        const hC = innerH * ((r.totale_costo || 0) / max);
        return (
          <g key={r.month}>
            <rect x={cx - barW - 1} y={H - padY - hF} width={barW} height={hF}
                  fill="url(#staff-bar-fatt)" rx="3">
              <title>{`Fatturato ${r.fatturato?.toFixed(0)}€`}</title>
            </rect>
            <rect x={cx + 1} y={H - padY - hC} width={barW} height={hC}
                  fill="url(#staff-bar-cost)" rx="3">
              <title>{`Costo ${r.totale_costo?.toFixed(0)}€`}</title>
            </rect>
            <text x={cx} y={H - 5} textAnchor="middle" fontSize="9" fill="#64748B">
              {["G","F","M","A","M","G","L","A","S","O","N","D"][r.month - 1]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
